<div class="page-title">
  <span translate>NEW_PORTAL_ACTIVITY</span>
</div>

<form [formGroup]="activityInfoForm" class="form">
  <div class="row">
    <!-- Activity Information Column -->
    <div class="col-sm-4 section">
      <h4 translate>ACTIVITY_INFORMATION</h4>

      <div class="form-group">
        <label for="title" class="col-form-label" translate>
          TITLE
          <span class="required" style="color: red !important; margin-left: 0">*</span>
        </label>
        <div>
          <input type="text" pInputText formControlName="title" />
        </div>
        <control-messages [control]="activityInfoForm.controls['title']"></control-messages>
      </div>

      <div class="form-group">
        <label for="description" class="col-form-label" translate>DESCRIPTION</label>
        <div>
          <textarea 
            formControlName="description" 
            class="form-control" 
            rows="4">
          </textarea>
        </div>
      </div>

      <div class="form-group">
        <label for="repetition" class="col-form-label" translate>
          REPETITION
        </label>
        <div>
          <p-select
            [options]="repetitionOptions" 
            formControlName="repetitionId"
            [filter]="true"
            [style]="{'width': '100%'}">
          </p-select>
        </div>
        <control-messages [control]="activityInfoForm.controls['repetitionId']"></control-messages>
      </div>

      <div class="form-group">
        <label for="checklist" class="col-form-label" translate>CHECKLIST</label>
        <div class="input-btn-group">
          <p-select
            [options]="checklistOptions" 
            formControlName="checklistId"
            (onChange)="onChecklistChange()"
            [filter]="true"
            [showClear]="true"
            [placeholder]="'SELECT_CHECKLIST' | translate"
            [style]="{'width': '100%'}">
          </p-select>
          <button
            type="button"
            (click)="displayOverlay.toggle($event)" class="kuba-icon-btn kuba-icon-btn--ghost kuba-icon-btn--row">
            <i class="pi pi-info-circle"></i>
          </button>
        </div>
      </div>

      <div class="form-group">
        <div class="attachments-container">
          <!-- Header -->
          <div class="attachments-header">
            <label class="attachments-title" translate>ACTIVITY_DOCUMENT</label>
            <input 
              type="file" 
              #fileInput
              multiple 
              (change)="onFileSelected($event)"
              style="display: none;">
            <button 
              type="button" 
              class="btn btn-outline-success add-file-btn"
              (click)="fileInput.click()">
              <span translate>ADD_FILES</span>
            </button>
          </div>

          <!-- File list -->
          <div class="file-list-container" *ngIf="uploadedFiles.length > 0">
            <div class="file-list-header">
              <span class="file-name-header" translate>FILE_NAME</span>
            </div>
            <div class="file-list">
              <div 
                class="file-item" 
                *ngFor="let file of uploadedFiles; let i = index">
                <div class="file-info">
                  <img [src]="getFileIcon(file)" alt="File" class="file-icon">
                  <span class="file-name">{{ file.name }}</span>
                </div>
                <div class="file-actions">
                  <button 
                    type="button" 
                    class="btn btn-icon delete-btn"
                    (click)="removeFile(i)"
                    title="Delete">
                    <i class="pi pi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div class="empty-state" *ngIf="uploadedFiles.length === 0">
            <p class="text-muted" translate>NO_FILES_UPLOADED</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Notification Settings Column -->
    <div class="col-sm-4 section">
      <h4 translate>NOTIFICATION_SETTINGS</h4>

      <div class="form-group">
        <label for="deadline" class="col-form-label" translate>
          DEADLINE
          <span class="required" style="color: red !important; margin-left: 0">*</span>
        </label>
        <div>
          <p-datepicker
            formControlName="deadline"
            [showIcon]="true"
            [placeholder]="'SELECT_DEADLINE' | translate"
            [icon]="'pi pi-calendar'"
            [dateFormat]="'dd/mm/yy'"
            inputId="deadline"
            [minDate]="minDate"
            [style]="{ width: '100%' }"
            required>
          </p-datepicker>
        </div>
        <control-messages [control]="activityInfoForm.controls['deadline']"></control-messages>
      </div>

      <div class="form-group">
        <label for="beforeDeadline" class="col-form-label" translate>
          NOTIFICATION_BEFORE_DEADLINE
        </label>
        <div>
          <p-select
            [options]="beforeDeadlineOptions" 
            formControlName="beforeDeadline"
            [style]="{'width': '100%'}">
          </p-select>
        </div>
      </div>

      <div class="form-group">
        <label for="afterDeadline" class="col-form-label" translate>
          NOTIFICATION_BELOW_FOLLOWUP
        </label>
        <div>
          <p-select
            [options]="afterDeadlineOptions" 
            formControlName="afterDeadline"
            [style]="{'width': '100%'}">
          </p-select>
        </div>
      </div>
    </div>

    <!-- Assignment Column -->
    <div class="col-sm-4 section">
      <h4 translate>ASSIGNMENT</h4>

      <div class="form-group">
        <div class="business-header">
          <label class="col-form-label" translate>SELECT_BUSINESSES</label>
          <div class="business-header-controls">
            <div class="business-counter" *ngIf="filteredBusinessList.length > 0">
              <span class="counter-text">
                {{ selectedBusinesses.length }}/{{ filteredBusinessList.length }}
                <span translate>SELECTED</span>
              </span>
            </div>
            <button 
              type="button" 
              class="btn btn-outline-success add-file-btn select-all-btn"
              (click)="toggleSelectAllBusinesses()"
              [disabled]="areAllBusinessesDisabled() || filteredBusinessList.length === 0">
              <span *ngIf="!isAllBusinessesSelected()" translate>SELECT_ALL</span>
              <span *ngIf="isAllBusinessesSelected()" translate>DESELECT_ALL</span>
            </button>
          </div>
        </div>
        <div class="business-search">
          <input 
            type="text" 
            pInputText 
            [ngModel]="businessSearchTerm"
            (input)="onBusinessSearch($event)"
            placeholder="{{ 'SEARCH_BY_NAME' | translate }}"
            [ngModelOptions]="{standalone: true}">
        </div>
      </div>

      <div class="business-table-wrapper">
        <div class="business-table-container">
          <p-table 
            [value]="businessTableData"
            [loading]="businessTableLoading"
            [rows]="businessTablePageSize"
            [paginator]="false"
            [lazy]="false"
            dataKey="id"
            [style]="{'width': '100%'}">
            
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 50px">
                  <p-checkbox 
                    [ngModel]="selectAllChecked"
                    [ngModelOptions]="{standalone: true}"
                    [binary]="true"
                    (onChange)="onSelectAllChange()"
                    [disabled]="areAllBusinessesDisabled()">
                  </p-checkbox>
                </th>
                <th translate>BUSINESS_NAME</th>
              </tr>
            </ng-template>
            
            <ng-template pTemplate="body" let-business>
              <tr [class.disabled-row]="isBusinessDisabled(business)">
                <td>
                  <p-checkbox 
                    [ngModel]="isBusinessSelected(business)"
                    [ngModelOptions]="{standalone: true}"
                    [binary]="true"
                    (onChange)="onBusinessCheckboxChange(business, $event)"
                    [disabled]="isBusinessDisabled(business)">
                  </p-checkbox>
                </td>
                <td 
                  [pTooltip]="getBusinessTooltip(business)"
                  [tooltipDisabled]="!isBusinessDisabled(business)"
                  tooltipPosition="left">
                  {{ business.companyName || business.name || business.CompanyName }}
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
        
        <!-- Pagination outside scrollable area -->
        <div class="business-pagination-container">
          <p-paginator
            [rows]="businessTablePageSize"
            [totalRecords]="filteredBusinessList.length"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="{{'SHOWING' | translate}} {last} {{'OF' | translate}} {totalRecords} {{'ENTRIES' | translate}}"
            (onPageChange)="onBusinessTablePageChange($event)"
            [style]="{'width': '100%'}">
          </p-paginator>
        </div>
      </div>
    </div>
  </div>
</form>

<!-- Action Buttons -->
<div class="action-btns-wrapper mt-4">
  <button 
    class="btn btn-outline-secondary" 
    [disabled]="isSavingActivities"
    (click)="goBack()">
    <i class="pi pi-arrow-left"></i>
    <span translate>BACK</span>
  </button>
  <button 
    class="btn btn-primary" 
    [disabled]="!activityInfoForm.valid || selectedBusinesses.length === 0 || isLoading || isSavingActivities"
    (click)="saveActivity()">
    <span *ngIf="!isLoading" translate>SAVE</span>
    <span *ngIf="isLoading">
      <i class="pi pi-spin pi-spinner me-2"></i>
      <span translate>SAVING</span>
    </span>
  </button>
</div>

<!-- Progress Loader for Activity Creation -->
<app-progress-loader 
  #progressLoader
  [config]="progressLoaderConfig">
</app-progress-loader>


<p-confirmDialog
  header="{{ 'CONFIRMATION' | translate }}"
  icon="pi pi-exclamation-triangle"
  width="425"
  acceptLabel="{{ 'CREATE' | translate }}"
  rejectLabel="{{ 'DISCARD' | translate }}"
></p-confirmDialog>

<p-popover #displayOverlay [dismissable]="true" appendTo="body">
  <h5>{{ "INFO" | translate }}</h5>
  <p>
    <span translate>IF_THE_CHECKLIST_IS_IN_UNDER_COMPLETION_WE_DO_NOT_HAVE_THE_OPTION_TO_CHANGE_THE_CHECKLIST_IN_DROPDOWN</span>
  </p>
</p-popover>
